import { useEffect, useState } from 'react';
import { heroSlides } from '../data/media.js';
import './Loader.css';

const MIN_VISIBLE_MS = 1400; // Long enough to read; short enough not to annoy.
const MAX_VISIBLE_MS = 5000; // Hard ceiling so a slow image never traps anyone.

/** Resolves once the image loads, or immediately on error. Never rejects. */
function preload(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = src;
  });
}

/**
 * Full-screen intro loader.
 *
 * Dismisses on whichever comes first: the first two hero images finishing
 * their download, or MAX_VISIBLE_MS. A loader that can outlast a bad network
 * is a loader that locks people out of the site, so the ceiling is not
 * optional.
 */
export default function Loader({ onDone }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const dismiss = () => {
      if (cancelled) return;
      cancelled = true;

      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);

      window.setTimeout(() => {
        setLeaving(true);
        // Matches the 700ms opacity transition in Loader.css.
        window.setTimeout(() => onDone?.(), 700);
      }, wait);
    };

    // Only the first two slides gate the reveal; the rest stream in behind it.
    Promise.all(heroSlides.slice(0, 2).map((s) => preload(s.src))).then(dismiss);

    const ceiling = window.setTimeout(dismiss, MAX_VISIBLE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(ceiling);
    };
  }, [onDone]);

  return (
    <div
      className={`loader on-dark${leaving ? ' loader--leaving' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Loading Metropolitan Church of God"
    >
      <div className="loader__bokeh" aria-hidden="true" />
      <div className="loader__grain" aria-hidden="true" />

      <div className="loader__inner">
        <svg
          className="loader__cross"
          viewBox="0 0 48 72"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          {/* Latin cross: upright with the crossbar set high, drawn as a single
              path so the glow filter applies evenly across the whole mark. */}
          <path
            d="M20 2h8v16h16v8H28v44h-8V26H4v-8h16V2z"
            fill="currentColor"
          />
        </svg>

        <div>
          <p className="loader__name">Metropolitan Church of God</p>
          <p className="loader__tagline">Detroit, Michigan</p>
        </div>

        <div className="loader__rule" aria-hidden="true" />
      </div>
    </div>
  );
}
